import type { Conversation, Folder } from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import { useUI } from '../state/ui.js';
import { formatDateTime } from '../lib/dom.js';
import { groupConversations } from '../lib/grouping.js';
import { lockScrollable } from '../lib/sidebarInteractions.js';
import { loadPinnedOrder, persistPinnedOrder, subscribePinnedOrder } from '../lib/pinnedOrder.js';
import { useSidebarDnd } from './SidebarDnd.js';
import { useSession } from '../state/session.js';
import { useAnalyticsTracking } from '../state/analytics.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { ContextMenu } from './ContextMenu.js';
import { Icon } from './Icon.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { MenuItem } from './ContextMenu.js';

interface ConversationItemProps {
  conversation: Conversation;
  active: boolean;
  folders: Folder[];
  menuOpen: boolean;
  hasAnyMenuOpen: boolean;
  /** Row belongs to the pinned group (can be reordered by dropping on it). */
  pinnedGroup: boolean;
  onSelect: () => void;
  onRename: () => void;
  onPin: () => void;
  onArchive: () => void;
  onDelete: () => void;
  onMoveToFolder: (folderId: string | null) => void;
  onMenuOpen: () => void;
  onMenuClose: () => void;
}

function ConversationItem({
  conversation,
  active,
  folders,
  menuOpen,
  hasAnyMenuOpen,
  pinnedGroup,
  onSelect,
  onRename,
  onPin,
  onArchive,
  onDelete,
  onMoveToFolder,
  onMenuOpen,
  onMenuClose,
}: ConversationItemProps) {
  const { t } = useI18n();
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [folderPickerPos, setFolderPickerPos] = useState<{ x: number; y: number } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [pressing, setPressing] = useState(false);
  const dnd = useSidebarDnd();
  const isDragSource = dnd.drag?.id === conversation.id;
  const dropMark =
    dnd.target?.kind === 'pinned-item' && dnd.target.id === conversation.id
      ? dnd.target.after
        ? ' is-drop-after'
        : ' is-drop-before'
      : '';

  const posRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const openMenuAt = (position: { x: number; y: number }) => {
    posRef.current = position;
    setMenuPos(position);
    onMenuOpen();
  };

  const folderMenuItems: MenuItem[] = folders.map((f) => ({
    key: f.id,
    label: f.name,
    icon: 'folder',
    onSelect: () => {
      setFolderPickerPos(null);
      onMoveToFolder(f.id);
    },
  }));
  if (conversation.folderId) {
    folderMenuItems.push({
      key: 'remove-from-folder',
      label: t('sidebar.removeFromFolder'),
      icon: 'folderPlus',
      onSelect: () => {
        setFolderPickerPos(null);
        onMoveToFolder(null);
      },
    });
  }

  const menuItems: MenuItem[] = [
    { key: 'rename', label: t('menu.rename'), icon: 'edit', onSelect: onRename },
    {
      key: 'pin',
      label: conversation.isPinned ? t('menu.unpin') : t('menu.pin'),
      icon: 'pin',
      onSelect: onPin,
    },
    {
      key: 'archive',
      label: conversation.isArchived ? t('menu.unarchive') : t('menu.archive'),
      icon: 'archive',
      onSelect: onArchive,
    },
    {
      key: 'addToFolder',
      label: t('menu.addToFolder'),
      icon: 'folder',
      onSelect: () => {
        setMenuPos(null);
        setFolderPickerPos(posRef.current);
      },
    },
    {
      key: 'delete',
      label: t('menu.delete'),
      icon: 'trash',
      danger: true,
      onSelect: () => setConfirmDelete(true),
    },
  ];

  return (
    <>
      <button
        type="button"
        className={`conv-item${active ? ' is-active' : ''}${isDragSource ? ' is-drag-source' : ''}${pressing ? ' is-pressing' : ''}${dropMark}${menuOpen ? ' is-pressed' : ''}`}
        data-dnd-conv={conversation.id}
        data-dnd-pinned={pinnedGroup ? '1' : undefined}
        onPointerDown={(event) => {
          if (hasAnyMenuOpen || menuPos !== null) return;
          posRef.current = { x: event.clientX, y: event.clientY };
          // Touch: hold → haptic + context menu; keep moving and it becomes a drag
          // (pin / unpin / into a folder). Mouse: just drag.
          dnd.startPress(
            event,
            {
              id: conversation.id,
              folderId: conversation.folderId,
              pinned: conversation.isPinned,
              title: conversation.title || t('common.empty'),
              ...(conversation.lastMessageAt
                ? { subtitle: formatDateTime(conversation.lastMessageAt) }
                : {}),
            },
            {
              onPressChange: setPressing,
              onMenu: openMenuAt,
              // Moving on while the menu is open turns the hold into a drag.
              onMenuClose: () => {
                setMenuPos(null);
                onMenuClose();
              },
            },
          );
        }}
        onClick={(event) => {
          // The click that follows a drag / press-and-release must not open the chat.
          if (dnd.consumeSuppressedClick()) {
            event.preventDefault();
            return;
          }
          onSelect();
        }}
        onContextMenu={(event) => {
          event.preventDefault();
          // On touch the long-press is the drag gesture; its menu opens on release.
          if (dnd.lastPointerType() !== 'mouse') return;
          openMenuAt({ x: event.clientX, y: event.clientY });
        }}
      >
        <span className="conv-title">{conversation.title || t('common.empty')}</span>
        {conversation.lastMessageAt && (
          <span className="conv-time">{formatDateTime(conversation.lastMessageAt)}</span>
        )}
        {conversation.isPinned && (
          <span className="conv-pin" role="img" aria-label={t('conversation.pinnedBadge')}>
            <Icon name="attach" size={14} />
          </span>
        )}
      </button>

      <ContextMenu
        open={menuPos !== null}
        x={menuPos?.x ?? 0}
        y={menuPos?.y ?? 0}
        items={menuItems}
        onClose={() => {
          setMenuPos(null);
          onMenuClose();
        }}
      />

      <ContextMenu
        open={folderPickerPos !== null}
        x={folderPickerPos?.x ?? 0}
        y={folderPickerPos?.y ?? 0}
        items={
          folderMenuItems.length > 0
            ? folderMenuItems
            : [
                {
                  key: 'no-folders',
                  label: t('sidebar.newFolder'),
                  icon: 'folderPlus',
                  onSelect: () => setFolderPickerPos(null),
                },
              ]
        }
        onClose={() => setFolderPickerPos(null)}
      />

      <ConfirmDialog
        open={confirmDelete}
        title={t('menu.deleteTitle')}
        message={t('menu.deleteConfirm')}
        onConfirm={() => {
          setConfirmDelete(false);
          onDelete();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
}

interface ConversationListProps {
  conversations: Conversation[];
  folders: Folder[];
  section: Conversation['section'];
  activeId: string | null;
  onSelect: (id: string) => void;
  /** When true, pinned conversations are NOT separated into their own group at
   *  the top — everything is sorted chronologically in one flat list. */
  flatList?: boolean;
  hideEmptyState?: boolean;
}

export function ConversationList({
  conversations,
  folders,
  section,
  activeId,
  onSelect,
  flatList = false,
  hideEmptyState = false,
}: ConversationListProps) {
  const { t } = useI18n();
  const { api } = useSession();
  const analytics = useAnalyticsTracking();
  const { refreshConversations, toast } = useUI();
  const listRef = useRef<HTMLDivElement>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [optimistic, setOptimistic] = useState<Record<string, Partial<Conversation>>>({});
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [pinnedOrder, setPinnedOrder] = useState<string[]>(() => loadPinnedOrder(section));

  // Merge optimistic overrides into the incoming list (memoised to stabilise the useEffect dep below)
  const mergedConversations = useMemo(
    () =>
      conversations.map((conv) =>
        optimistic[conv.id] ? { ...conv, ...optimistic[conv.id] } : conv,
      ),
    [conversations, optimistic],
  );
  const groups = groupConversations(mergedConversations, new Date(), flatList);

  // Re-read the order when the section changes, and whenever a drop elsewhere in
  // the sidebar (another list instance) saves a new one.
  useEffect(() => {
    setPinnedOrder(loadPinnedOrder(section));
    return subscribePinnedOrder((changed) => {
      if (changed === section) setPinnedOrder(loadPinnedOrder(section));
    });
  }, [section]);

  useEffect(() => {
    const nextPinnedIds = mergedConversations
      .filter((conversation) => conversation.isPinned)
      .map((conversation) => conversation.id);
    // Chats this list has never heard of keep their slot: a drop just reserved one
    // for a chat that only shows up as pinned once the refreshed list arrives.
    const knownUnpinned = new Set(
      mergedConversations.filter((conversation) => !conversation.isPinned).map((c) => c.id),
    );
    setPinnedOrder((prev) => {
      const preserved = prev.filter((id) => nextPinnedIds.includes(id) || !knownUnpinned.has(id));
      const additions = nextPinnedIds.filter((id) => !preserved.includes(id));
      const next = [...preserved, ...additions];
      if (next.length === prev.length && next.every((id, index) => prev[index] === id)) {
        return prev;
      }
      return next;
    });
  }, [mergedConversations]);

  // Only the list that actually shows the pinned group owns the stored order. Other
  // instances (recent chats, folders) hold a copy and must not overwrite it.
  const ownsPinnedOrder = !flatList && mergedConversations.some((conv) => conv.isPinned);
  useEffect(() => {
    if (!ownsPinnedOrder) return;
    // Only a mirror of this list's state, so subscribers are not notified.
    persistPinnedOrder(section, pinnedOrder);
  }, [pinnedOrder, section, ownsPinnedOrder]);

  useEffect(() => {
    if (menuOpenId === null) return;
    const sidebarBody = listRef.current?.closest(
      '.sidebar-body, .sidebar-pane-menu',
    ) as HTMLElement | null;
    const mainView = document.querySelector<HTMLElement>('.main-view');
    const unlockSidebar = lockScrollable(sidebarBody);
    const unlockMain = lockScrollable(mainView);
    return () => {
      unlockSidebar();
      unlockMain();
    };
  }, [menuOpenId]);

  function orderedGroupConversations(group: (typeof groups)[number]) {
    if (group.key !== 'pinned') return group.conversations;
    const orderIndex = new Map(pinnedOrder.map((id, index) => [id, index]));
    return [...group.conversations].sort((a, b) => {
      const aIndex = orderIndex.get(a.id) ?? Number.MAX_SAFE_INTEGER;
      const bIndex = orderIndex.get(b.id) ?? Number.MAX_SAFE_INTEGER;
      return aIndex - bIndex;
    });
  }

  function handleRename(conv: Conversation) {
    setRenaming(conv.id);
    setRenameValue(conv.title);
  }

  async function submitRename(id: string) {
    if (renameValue.trim()) {
      await api.updateConversation(id, { title: renameValue.trim() }).catch(() => undefined);
      refreshConversations();
    }
    setRenaming(null);
  }

  async function handlePin(conv: Conversation) {
    const next = !conv.isPinned;
    if (!next) setPinnedOrder((prev) => prev.filter((id) => id !== conv.id));
    setOptimistic((prev) => ({ ...prev, [conv.id]: { isPinned: next } }));
    try {
      await api.updateConversation(conv.id, { isPinned: next });
      if (next) analytics.trackFavoriteAdded();
      toast(next ? t('menu.pin') : t('menu.unpin'), 'success');
      refreshConversations();
    } catch {
      setOptimistic((prev) => ({ ...prev, [conv.id]: { isPinned: conv.isPinned } }));
      toast(t('common.error'), 'error');
    } finally {
      setOptimistic((prev) => {
        const n = { ...prev };
        delete n[conv.id];
        return n;
      });
    }
  }

  async function handleArchive(conv: Conversation) {
    const next = !conv.isArchived;
    setOptimistic((prev) => ({ ...prev, [conv.id]: { isArchived: next } }));
    try {
      await api.updateConversation(conv.id, { isArchived: next });
      toast(next ? t('menu.archive') : t('menu.unarchive'), 'success');
      refreshConversations();
    } catch {
      setOptimistic((prev) => ({ ...prev, [conv.id]: { isArchived: conv.isArchived } }));
      toast(t('common.error'), 'error');
    } finally {
      setOptimistic((prev) => {
        const n = { ...prev };
        delete n[conv.id];
        return n;
      });
    }
  }

  async function handleDelete(id: string) {
    try {
      await api.deleteConversation(id);
      toast(t('menu.delete'), 'success');
      refreshConversations();
    } catch {
      toast(t('common.error'), 'error');
    }
  }

  async function handleMoveToFolder(conv: Conversation, folderId: string | null) {
    setOptimistic((prev) => ({ ...prev, [conv.id]: { folderId } }));
    try {
      await api.updateConversation(conv.id, { folderId });
      toast(folderId ? t('menu.addToFolder') : t('sidebar.removeFromFolder'), 'success');
      refreshConversations();
    } catch {
      setOptimistic((prev) => ({ ...prev, [conv.id]: { folderId: conv.folderId } }));
      toast(t('common.error'), 'error');
    } finally {
      setOptimistic((prev) => {
        const n = { ...prev };
        delete n[conv.id];
        return n;
      });
    }
  }

  if (conversations.length === 0) {
    if (hideEmptyState) return null;
    return <p className="conv-empty">{t('sidebar.noConversations')}</p>;
  }

  return (
    <div ref={listRef} className={`conv-list${menuOpenId !== null ? ' has-open-menu' : ''}`}>
      {groups.map((group) => (
        <div key={group.key} className="conv-group">
          <p className="conv-group-label">{t(group.labelKey)}</p>
          {orderedGroupConversations(group).map((conv) =>
            renaming === conv.id ? (
              <form
                key={conv.id}
                className="conv-rename"
                onSubmit={(event) => {
                  event.preventDefault();
                  void submitRename(conv.id);
                }}
              >
                <input
                  className="field conv-rename-input"
                  value={renameValue}
                  autoFocus
                  onChange={(event) => setRenameValue(event.target.value)}
                  onBlur={() => void submitRename(conv.id)}
                />
              </form>
            ) : (
              <ConversationItem
                key={conv.id}
                conversation={conv}
                folders={folders}
                menuOpen={menuOpenId === conv.id}
                hasAnyMenuOpen={menuOpenId !== null}
                pinnedGroup={group.key === 'pinned'}
                active={conv.id === activeId}
                onSelect={() => onSelect(conv.id)}
                onRename={() => handleRename(conv)}
                onPin={() => void handlePin(conv)}
                onArchive={() => void handleArchive(conv)}
                onDelete={() => void handleDelete(conv.id)}
                onMoveToFolder={(folderId) => void handleMoveToFolder(conv, folderId)}
                onMenuOpen={() => setMenuOpenId(conv.id)}
                onMenuClose={() =>
                  setMenuOpenId((current) => (current === conv.id ? null : current))
                }
              />
            ),
          )}
        </div>
      ))}
    </div>
  );
}
